/**
 * Admin Events Hub: iOS-style native mobile app.
 * Segmented control with 2 views: Calendar, Live.
 * Each view fully replaces the content area.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import { PageLoader } from "../../../components/PageLoader.tsx";
import {
  IconPlus,
  IconCalendar,
  IconDocument,
  IconAlertTriangle,
} from "../../../components/Icons.tsx";
import { getTelegram, haptic } from "../../../lib/telegram.ts";
import { getErrorMessage } from "../../../lib/graphql.ts";
import { EventsCalendarView } from "./EventsCalendarView.tsx";
import { EventsLiveStream } from "./EventsLiveStream.tsx";
import { useAdminEvents, useCreateEvent } from "./hooks";

type ViewMode = "calendar" | "live";

const VIEW_MODE_STORAGE_KEY = "admin_events_view_mode";

export function AdminEventsPage() {
  const navigate = useNavigate();
  const tg = getTelegram();

  const { events, isLoading, error, refetch } = useAdminEvents();
  const { createEvent, isLoading: isCreating } = useCreateEvent();

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = sessionStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (saved === "live") return "live";
    return "calendar";
  });

  // Save view mode
  useEffect(() => {
    sessionStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  // BackButton handling
  useEffect(() => {
    if (!tg) return;

    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin");
      }
    };
    tg.BackButton.onClick(handleBack);

    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  // Create new event
  const handleCreate = async () => {
    const result = await createEvent({
      title: "Новое мероприятие",
      startsAt: new Date().toISOString(),
      status: "DRAFT",
    });
    if (result) {
      haptic("success");
      navigate(`/admin/events/${result.id}`);
    } else {
      haptic("error");
    }
  };

  const handleModeChange = (mode: ViewMode) => {
    haptic("light");
    setViewMode(mode);
  };

  if (isLoading && events.length === 0) return <PageLoader message="Загрузка мероприятий..." />;

  if (error) {
    return (
      <Layout>
        <div className="card p-5 text-center">
          <IconAlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
          <h2 className="text-lg font-bold text-slate-900 mb-2">
            Ошибка загрузки
          </h2>
          <p className="text-sm text-slate-500 mb-4">{getErrorMessage(error, "Не удалось загрузить мероприятия")}</p>
          <button onClick={refetch} className="btn-primary">
            Попробовать снова
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600">
          <IconCalendar className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-slate-900">Мероприятия</h1>
          <p className="text-[12px] text-slate-500">{events.length} всего</p>
        </div>
        <button
          type="button"
          onClick={() => {
            haptic("light");
            handleCreate();
          }}
          disabled={isCreating}
          className="w-10 h-10 rounded-xl bg-[var(--color-accent)] text-white flex items-center justify-center active:scale-95 transition-transform disabled:opacity-50"
        >
          {isCreating ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <IconPlus className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Segmented control: Calendar / Live */}
      <div className="flex p-1 bg-slate-100 rounded-xl mb-4">
        <button
          type="button"
          onClick={() => handleModeChange("calendar")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-[13px] font-medium transition-all ${
            viewMode === "calendar"
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-500"
          }`}
        >
          <IconCalendar className="w-4 h-4" />
          Календарь
        </button>
        <button
          type="button"
          onClick={() => handleModeChange("live")}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-[13px] font-medium transition-all ${
            viewMode === "live"
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-500"
          }`}
        >
          <IconDocument className="w-4 h-4" />
          Лента
        </button>
      </div>

      {/* Content area */}
      {viewMode === "calendar" && (
        <EventsCalendarView events={events} onRefresh={refetch} />
      )}
      {viewMode === "live" && (
        <EventsLiveStream events={events} onRefresh={refetch} />
      )}

      <div className="h-4" />
    </Layout>
  );
}
